import {HttpClient} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {EMPTY} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {isErrorLike, serializeError} from 'serialize-error';
import {AppConfigService} from '../../services/config.service';

enum LogRecLevel {
    LOG,
    INFO,
    WARN,
    ERROR
}

interface LogRec {
    ts: number;
    level: LogRecLevel;
    msg: string;
}

/**
 * Ref.: https://github.com/winstonjs/winston?tab=readme-ov-file#logging-levels
 */
const LOG_REC_LEVEL_TO_API_LEVEL_DICT: Record<LogRecLevel, string> = {
    [LogRecLevel.LOG]:   'trace',
    [LogRecLevel.INFO]:  'info',
    [LogRecLevel.WARN]:  'warn',
    [LogRecLevel.ERROR]: 'error'
};

@Injectable()
export class LoggerService {

    private logStartIndex: number = 0;

    private readonly logs: LogRec[] = [];

    private readonly maxLogCount: number = 1000;

    private readonly appConfigService = inject(AppConfigService);
    private readonly httpClient = inject(HttpClient);

    private readonly logApiUrl: string = this.appConfigService.api['log'];

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly isLoggerApiEnabled = this.appConfigService.appConfig.isLoggerApiEnabled;

    constructor() {
        (window as any).loggerService = this; // TODO
    }


    public log(...args: any[]): void {
        console.log(...args);

        this.addLogRec(LogRecLevel.LOG, this.createLogRecMsg(args));
    }

    public info(...args: any[]): void {
        console.info(...args);

        this.addLogRec(LogRecLevel.INFO, this.createLogRecMsg(args));
    }

    public warn(...args: any[]): void {
        console.warn(...args);

        this.addLogRec(LogRecLevel.WARN, this.createLogRecMsg(args));
    }

    public error(...args: any[]): void {
        console.error(...args);

        this.addLogRec(LogRecLevel.ERROR, this.createLogRecMsg(args));
    }

    public getAllLogs(): LogRec[] {
        const fixOrder = () => {
            const ordered = this.logs.slice(this.logStartIndex);
            ordered.push(...this.logs.slice(0, this.logStartIndex));

            return ordered;
        };

        return (this.logStartIndex === 0)
            ? [...this.logs]
            : fixOrder();
    }

    public getIterable(): Iterable<LogRec> {
        return {
            [Symbol.iterator]: () => {
                let i = this.logStartIndex;
                let counter = 0;

                return {
                    next: () => {
                        const done = counter >= this.logs.length;
                        const value = done ? undefined : this.logs[i];

                        i = (i < this.logs.length - 1) ? i + 1 : 0;

                        if (!done) {
                            counter++;
                        }

                        return {done, value};
                    }
                };
            }
        };
    }

    public getReverseIterable(): Iterable<LogRec> {
        return {
            [Symbol.iterator]: () => {
                let i = this.logStartIndex
                    ? this.logStartIndex - 1
                    : this.logs.length - 1;
                let counter = 0;

                return {
                    next: () => {
                        const done = counter >= this.logs.length;
                        const value = done ? undefined : this.logs[i];

                        i = (i > 0) ? i - 1 : this.logs.length - 1;

                        if (!done) {
                            counter++;
                        }

                        return {done, value};
                    }
                };
            }
        };
    }

    private addLogRec(level: LogRecLevel, msg: string): void {
        const ts = Date.now();
        const logRec: LogRec = {
            ts,
            level,
            msg
        };

        if (this.logs.length < this.maxLogCount) {
            this.logs.push(logRec);
        } else {
            this.logs[this.logStartIndex] = logRec;
            this.logStartIndex++;

            if (this.logStartIndex >= this.maxLogCount) {
                this.logStartIndex = 0;
            }
        }

        this.sendLogRecToApi(logRec);
    }

    private createLogRecMsg(args: any[]): string {
        return args
            .map(

                // isErrorLike checks presence of the 'stack' field, which may not present in Error.
                arg => isErrorLike(arg) || arg instanceof Error
                    ? this.safeJsonStringify(serializeError(arg))
                    : this.safeJsonStringify(arg)
            )
            .join('\r\n');
    }

    private sendLogRecToApi(logRec: LogRec): void {
        if (this.isLoggerApiEnabled) {
            if (this.logApiUrl) {
                this.httpClient.post(this.logApiUrl, {
                    level:   LOG_REC_LEVEL_TO_API_LEVEL_DICT[logRec.level],
                    message: logRec.msg
                }).pipe(
                    catchError(() => EMPTY)
                ).subscribe();
            }
        }
    }

    private safeJsonStringify(value: any): string {
        const MAX_ARG_STRING_LENGTH = 65536;
        let result: string;

        const bigIntReplacer = (_key: string, val: any): any => {
            if (typeof val === 'bigint') {
                return val.toString() + 'n';
            }
            return val;
        };

        try {
            result = JSON.stringify(value, bigIntReplacer);

            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (_e) {
            result = this.fallbackStringify(value);
        }

        if (result.length > MAX_ARG_STRING_LENGTH) {
            const truncatedSize = result.length - MAX_ARG_STRING_LENGTH;
            result = result.slice(0, MAX_ARG_STRING_LENGTH) + `...[truncated ${truncatedSize} bytes]`;
        }

        return result;
    }

    private fallbackStringify(value: any, seen: WeakSet<any> = new WeakSet<any>()): string {
        if (value === null) return 'null';
        if (value === undefined) return 'undefined';
        if (typeof value === 'function') return `[Function: ${value.name || 'anonymous'}]`;
        if (typeof value === 'symbol') return value.toString();
        if (typeof value === 'bigint') return value.toString() + 'n';
        if (typeof value === 'number' || typeof value === 'boolean') return String(value);
        if (typeof value === 'string') return `"${value}"`;

        if (typeof value === 'object') {
            if (seen.has(value)) return '[Circular]';
            seen.add(value);

            if (Array.isArray(value)) {
                const items = value.map(item => this.fallbackStringify(item, seen));
                return `[${items.join(',')}]`;
            }

            try {
                const keys = Object.keys(value);
                const pairs = keys.map(key => `"${key}":${this.fallbackStringify(value[key], seen)}`);
                return `{${pairs.join(',')}}`;
            } catch {
                return `[Object: ${Object.prototype.toString.call(value)}]`;
            }
        }

        return String(value);
    }

}
